import { useMutation } from "@tanstack/react-query";
import { useActivityHistoryInvalidate } from "PFCore/hooks/queries/activities/history/use_activity_history_invalidate";
import { useMatchesShortlistReplace } from "PFCore/hooks/queries/matches/use_matches_shortlist_replace";
import { mutationKeys } from "PFCore/hooks/queries/shortlists/mutation_keys";
import { newShortlist } from "PFCore/services/shortlist";
import { ShortlistMinimized, ShortlistSuggested } from "PFTypes";
import { Id } from "PFTypes/id";
import { MutationOptions } from "PFTypes/request_options";

import { useShortlistsInvalidate } from "./use_shortlists_invalidate";

type ShortlistCreateParams = {
  activityId: Id;
  profileId: Id;
  named?: boolean;
  suggested?: ShortlistSuggested;
};

export const useShortlistCreate = (
  options: MutationOptions<ShortlistCreateParams, ShortlistMinimized> = {},
  withInvalidate = true
) => {
  const { invalidateShortlists } = useShortlistsInvalidate();
  const invalidateHistory = useActivityHistoryInvalidate();
  const { replaceMatchesShortlistInCache } = useMatchesShortlistReplace();

  return useMutation({
    mutationKey: mutationKeys.create(),
    mutationFn: async ({
      activityId,
      profileId,
      named = true,
      suggested
    }: ShortlistCreateParams): Promise<ShortlistMinimized> =>
      newShortlist(activityId, profileId, named, suggested),
    ...options,
    onSuccess: (...args) => {
      const [shortlist, variables] = args;
      if (withInvalidate) {
        invalidateShortlists(variables.activityId);
        invalidateHistory(variables.activityId);
      }
      replaceMatchesShortlistInCache(shortlist as ShortlistMinimized);

      options?.onSuccess?.(...args);
    }
  });
};
